/**
 * Published facts recognised in pasted product text (plan §3 step 1): the highlights the user
 * sees before and after analysis. After an analysis the proposal's own text spans win; this
 * recogniser only previews what the analysis will read as published.
 */
export interface PublishedFact {
  /** Field key: W, H, D, stockThickness, shelfCount, material, finish. */
  field: string;
  text: string;
  span: [number, number];
  value: number | string;
  unit: string | null;
  /** Millimetres, for lengths. */
  mm?: number;
}

const NUMBER_WORDS: Record<string, number> = { zero: 0, one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10 };
const toMmFrom = (v: number, unit: string): number => (unit === 'cm' ? v * 10 : unit === 'm' ? v * 1000 : unit === 'in' ? v * 25.4 : v);
const num = (s: string): number => Number(s.replace(',', '.'));

export function recogniseFacts(text: string): PublishedFact[] {
  if (!text) return [];
  const out: PublishedFact[] = [];
  const taken: [number, number][] = [];
  const free = (a: number, b: number) => !taken.some(([x, y]) => a < y && x < b);
  const add = (f: PublishedFact) => { if (free(f.span[0], f.span[1])) { out.push(f); taken.push(f.span); } };

  // "W 90 × H 180 × D 32 cm" (labelled triple, unit at the end)
  const tri = /\bW\s*(\d+(?:[.,]\d+)?)\s*[×x]\s*H\s*(\d+(?:[.,]\d+)?)\s*[×x]\s*D\s*(\d+(?:[.,]\d+)?)\s*(mm|cm|m)\b/i.exec(text);
  if (tri) {
    const unit = tri[4]!.toLowerCase();
    const base = tri.index;
    const whole = tri[0];
    for (const [key, label, value] of [['W', 'W', tri[1]!], ['H', 'H', tri[2]!], ['D', 'D', tri[3]!]] as const) {
      const re = new RegExp(`${label}\\s*${value.replace('.', '\\.')}`);
      const m = re.exec(whole);
      if (!m) continue;
      let start = base + m.index;
      let end = start + m[0].length;
      if (key === 'D') end = base + whole.length; // "D 32 cm" carries the unit
      if (key === 'W' && start < base) start = base;
      add({ field: key, text: text.slice(start, end), span: [start, end], value: num(value), unit, mm: toMmFrom(num(value), unit) });
    }
  } else {
    // "90 × 180 × 32 cm" (unlabelled: width × height × depth)
    const plain = /\b(\d+(?:[.,]\d+)?)\s*[×x]\s*(\d+(?:[.,]\d+)?)\s*[×x]\s*(\d+(?:[.,]\d+)?)\s*(mm|cm|m)\b/i.exec(text);
    if (plain) {
      const unit = plain[4]!.toLowerCase();
      const span: [number, number] = [plain.index, plain.index + plain[0].length];
      add({ field: 'W', text: plain[0], span, value: num(plain[1]!), unit, mm: toMmFrom(num(plain[1]!), unit) });
    }
    for (const [key, words] of [['W', 'width|wide'], ['H', 'height|high|tall'], ['D', 'depth|deep']] as const) {
      const re = new RegExp(`\\b(?:${words})\\s*:?\\s*(\\d+(?:[.,]\\d+)?)\\s*(mm|cm|m)\\b`, 'i');
      const m = re.exec(text);
      if (m) add({ field: key, text: m[0], span: [m.index, m.index + m[0].length], value: num(m[1]!), unit: m[2]!.toLowerCase(), mm: toMmFrom(num(m[1]!), m[2]!.toLowerCase()) });
    }
  }
  const mat = /\b(?:solid\s+)?birch\s+plywood\b|\bplywood\b/i.exec(text);
  if (mat) add({ field: 'material', text: mat[0], span: [mat.index, mat.index + mat[0].length], value: 'Birch plywood', unit: null });
  const thick = /\b(\d+(?:[.,]\d+)?)\s*mm\b/i.exec(text);
  if (thick && num(thick[1]!) >= 9 && num(thick[1]!) <= 30) add({ field: 'stockThickness', text: thick[0], span: [thick.index, thick.index + thick[0].length], value: num(thick[1]!), unit: 'mm', mm: num(thick[1]!) });
  const fin = /\bclear\s+(?:matt\s+|satin\s+)?lacquer\b|\blacquer(?:ed)?\b|\boiled\b/i.exec(text);
  if (fin) add({ field: 'finish', text: fin[0], span: [fin.index, fin.index + fin[0].length], value: 'Clear matt lacquer', unit: null });
  const sh = /\b(\d+|zero|one|two|three|four|five|six|seven|eight|nine|ten)\s+(?:fixed\s+|adjustable\s+)?shel(?:f|ves)\b/i.exec(text);
  if (sh) {
    const w = sh[1]!.toLowerCase();
    const n = /^\d+$/.test(w) ? Number(w) : NUMBER_WORDS[w] ?? 0;
    add({ field: 'shelfCount', text: sh[0], span: [sh.index, sh.index + sh[0].length], value: n, unit: null });
  }
  return out.sort((a, b) => a.span[0] - b.span[0]);
}

export interface TextPiece { text: string; field: string | null }

/** Split text into plain and highlighted pieces (for `<mark data-fact>` rendering). */
export function highlightPieces(text: string, facts: readonly Pick<PublishedFact, 'field' | 'span' | 'text'>[]): TextPiece[] {
  if (!text) return [];
  const spans = facts
    .filter((f) => f.span[0] >= 0 && f.span[1] <= text.length && f.span[1] > f.span[0])
    .slice().sort((a, b) => a.span[0] - b.span[0]);
  const out: TextPiece[] = [];
  let at = 0;
  for (const f of spans) {
    if (f.span[0] < at) continue;
    if (f.span[0] > at) out.push({ text: text.slice(at, f.span[0]), field: null });
    out.push({ text: text.slice(f.span[0], f.span[1]), field: f.field });
    at = f.span[1];
  }
  if (at < text.length) out.push({ text: text.slice(at), field: null });
  return out;
}
